import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Users, RefreshCw, Play, Pause, AlertCircle } from 'lucide-react';
import { bcStoreForEnv } from '../services/env';
import { bcApiStore, BASE_URL } from './BigCommerce/siteContentShared';

// PDP Live Viewers - real viewer counts across all product pages, per
// environment, fed by the pdp-presence Netlify function. Two bases:
//   GA4     - distinct devices that opened the PDP inside a trailing window
//             (default 30 min), mirroring GA4 Realtime active users. No leave
//             signal, so a count only decays when rows age out of the window.
//   Focus   - one count = one storefront tab currently FOCUSED on that PDP.
// Collected independently of GA4 so it can be used to sanity-check GA4's own
// realtime numbers rather than just echoing them.

const TH = { textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50)', position: 'sticky', top: 0, whiteSpace: 'nowrap' };
const TD = { padding: '8px 10px', fontSize: 12.5, borderBottom: '1px solid var(--gray-100)', color: 'var(--gray-800)', verticalAlign: 'top' };
const CARD = { background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' };

const ENVS = [
  { key: 'prod', label: 'Production', color: 'var(--orange)' },
  { key: 'sb1', label: 'Sandbox-1', color: 'var(--navy)' },
  { key: 'sb2', label: 'Sandbox-2', color: 'var(--navy)' },
];
const BASES = [
  { key: 'recent', label: 'GA4 (last N min)' },
  { key: 'focus', label: 'Focused now' },
];
const POLL_MS = 8000;
const PRESENCE_URL = BASE_URL + '/pdp-presence';

function EnvToggle({ env, onChange }) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--gray-300)', borderRadius: 6, overflow: 'hidden' }}>
      {ENVS.map(function (e) {
        return (
          <button key={e.key} onClick={function () { onChange(e.key); }}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none',
              background: env === e.key ? e.color : '#fff',
              color: env === e.key ? '#fff' : 'var(--gray-700)', cursor: 'pointer',
            }}>
            {e.label}
          </button>
        );
      })}
    </div>
  );
}

export default function PdpLiveViewers() {
  const [env, setEnv] = useState('prod');
  const [basis, setBasis] = useState('recent');
  const [windowMinutes, setWindowMinutes] = useState(30);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [paused, setPaused] = useState(false);

  // id -> name cache, per env (BC product list is stable across a session).
  const nameCacheRef = useRef({});

  const loadNames = useCallback(async function (targetEnv) {
    if (nameCacheRef.current[targetEnv]) return nameCacheRef.current[targetEnv];
    const store = bcStoreForEnv(targetEnv);
    const map = new Map();
    let page = 1;
    while (true) {
      const res = await bcApiStore(store, 'GET', '/v3/catalog/products?include_fields=name&page=' + page + '&limit=250');
      ((res && res.data) || []).forEach(function (p) { map.set(String(p.id), p.name); });
      if (!res || !res.meta || !res.meta.pagination || page >= res.meta.pagination.total_pages) break;
      page++;
    }
    nameCacheRef.current[targetEnv] = map;
    return map;
  }, []);

  const load = useCallback(async function (targetEnv, targetBasis) {
    setLoading(true);
    try {
      const res = await fetch(PRESENCE_URL + '?summary=1&env=' + encodeURIComponent(targetEnv)
        + '&basis=' + encodeURIComponent(targetBasis || 'recent'));
      const data = await res.json();
      if (!res.ok) throw new Error((data && data.error) || 'presence fetch failed');
      if (data && Number.isFinite(data.windowMinutes)) setWindowMinutes(data.windowMinutes);
      let names = new Map();
      try { names = await loadNames(targetEnv); } catch (e) { /* names best-effort */ }
      const merged = ((data && data.products) || []).map(function (p) {
        return {
          productId: p.productId,
          count: p.count,
          name: names.get(String(p.productId)) || ('Product ' + p.productId),
        };
      });
      setRows(merged);
      setTotal((data && data.total) || 0);
      setUpdatedAt(new Date());
      setError('');
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [loadNames]);

  useEffect(function () { load(env, basis); }, [env, basis, load]);

  useEffect(function () {
    if (paused) return undefined;
    const id = setInterval(function () { load(env, basis); }, POLL_MS);
    return function () { clearInterval(id); };
  }, [env, basis, paused, load]);

  const btnStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid var(--gray-300)', borderRadius: 6, background: '#fff', color: 'var(--gray-700)', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
  const tileLabel = { fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em' };
  const tileNum = { fontSize: 30, fontWeight: 800, color: 'var(--navy)', lineHeight: 1.1 };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <h1 style={{ color: 'var(--navy)', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Users size={22} /> PDP Live Viewers
        </h1>
        <EnvToggle env={env} onChange={setEnv} />
        <div style={{ display: 'inline-flex', border: '1px solid var(--gray-300)', borderRadius: 6, overflow: 'hidden' }}>
          {BASES.map(function (b) {
            return (
              <button key={b.key} onClick={function () { setBasis(b.key); }}
                style={{
                  padding: '6px 12px', fontSize: 12, fontWeight: 600, border: 'none',
                  background: basis === b.key ? 'var(--gray-700)' : '#fff',
                  color: basis === b.key ? '#fff' : 'var(--gray-700)', cursor: 'pointer',
                }}>
                {b.key === 'recent' ? 'GA4 (last ' + windowMinutes + ' min)' : b.label}
              </button>
            );
          })}
        </div>
        <button onClick={function () { load(env, basis); }} style={btnStyle}>
          <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} /> Refresh
        </button>
        <button onClick={function () { setPaused(function (p) { return !p; }); }} style={btnStyle}>
          {paused ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Play size={13} /> Resume</span>
                  : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Pause size={13} /> Pause</span>}
        </button>
      </div>

      <p style={{ color: 'var(--gray-500)', fontSize: 12.5, margin: '0 0 16px', maxWidth: 720 }}>
        {basis === 'recent' ? (
          <>
            GA4 basis - one count = one distinct device that opened that product page within the
            last {windowMinutes} minutes. Matches GA4 Realtime active users: there is no leave signal,
            so closing the tab does not remove anyone; counts decay only as rows age out of the window.
            Reloads and extra tabs on one device collapse to a single count.
          </>
        ) : (
          <>
            Focus basis - one count = one storefront tab currently focused on that product page.
            Backgrounded or blurred tabs drop off within ~30s.
          </>
        )}
        {' '}Collected independently of GA4; use to cross-check GA4 realtime.
        Auto-refreshes every {POLL_MS / 1000}s.
      </p>

      <div style={{ display: 'flex', gap: 16, alignItems: 'stretch', marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ ...CARD, borderTop: '3px solid var(--orange)', padding: '12px 18px', minWidth: 160 }}>
          <div style={tileLabel}>{basis === 'recent' ? 'Active users' : 'Total viewers'}</div>
          <div style={tileNum}>{total}</div>
        </div>
        <div style={{ ...CARD, borderTop: '3px solid var(--navy)', padding: '12px 18px', minWidth: 160 }}>
          <div style={tileLabel}>Active PDPs</div>
          <div style={tileNum}>{rows.length}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', color: 'var(--gray-500)', fontSize: 12 }}>
          {updatedAt ? 'Updated ' + updatedAt.toLocaleTimeString() : 'Loading...'}
        </div>
      </div>

      {error ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fde6e6', color: '#a51d1d', border: '1px solid #f5b5b5', borderRadius: 6, padding: '8px 12px', fontSize: 12.5, marginBottom: 16 }}>
          <AlertCircle size={15} /> {error}
        </div>
      ) : null}

      <div style={CARD}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={TH}>Product</th>
              <th style={{ ...TH, width: 120 }}>Product ID</th>
              <th style={{ ...TH, width: 140, textAlign: 'right' }}>{basis === 'recent' ? 'Active users' : 'Live viewers'}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td style={{ ...TD, textAlign: 'center', color: 'var(--gray-500)', padding: '24px 10px' }} colSpan={3}>
                {basis === 'recent'
                  ? 'No product pages have been opened in the last ' + windowMinutes + ' minutes.'
                  : 'No product pages have focused viewers right now.'}
              </td></tr>
            ) : null}
            {rows.map(function (r) {
              return (
                <tr key={r.productId}>
                  <td style={TD}>{r.name}</td>
                  <td style={{ ...TD, color: 'var(--gray-500)', fontFamily: '"SF Mono", Menlo, Consolas, monospace' }}>{r.productId}</td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700, fontSize: 14, color: 'var(--navy)' }}>{r.count}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
